import { useState, useEffect } from "react";
import { X, CreditCard } from "lucide-react";
import { supabase } from "../integrations/supabase/client";
import { toast } from "sonner";

interface EditSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  subscription: {
    id?: string;
    plan?: string;
    status?: string;
    current_period_start?: string;
    current_period_end?: string;
  } | null;
  onSuccess: () => void;
}

const plans = [
  { id: "starter", name: "Starter", price: 149 },
  { id: "pro", name: "Pro", price: 349 },
  { id: "business", name: "Business", price: 799 },
  { id: "enterprise", name: "Enterprise", price: 1999 },
];

const statuses = [
  { id: "active", name: "Ativo" },
  { id: "trialing", name: "Trial" },
  { id: "past_due", name: "Pendente" },
  { id: "canceled", name: "Cancelado" },
  { id: "suspended", name: "Suspenso" },
];

export function EditSubscriptionModal({
  isOpen,
  onClose,
  userId,
  subscription,
  onSuccess,
}: EditSubscriptionModalProps) {
  const [formData, setFormData] = useState({
    plan: "starter",
    status: "active",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (subscription) {
      setFormData({
        plan: subscription.plan || "starter",
        status: subscription.status || "active",
      });
    }
  }, [subscription, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    setLoading(true);
    try {
      const payload = {
        plan: formData.plan,
        status: formData.status,
      };

      const { error } = await supabase
        .from("subscriptions")
        .update(payload)
        .eq("user_id", userId);

      if (error) throw error;

      toast.success("Subscrição atualizada com sucesso!");
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Erro ao atualizar";
      console.error("Error updating subscription:", error);
      toast.error("Erro ao atualizar subscrição: " + message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { error } = await supabase.from("subscriptions").insert({
        user_id: userId,
        plan: formData.plan,
        status: formData.status,
      });
      if (error) throw error;
      toast.success("Subscrição criada com sucesso!");
      onSuccess();
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro ao criar";
      toast.error("Erro ao criar subscrição: " + message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const hasSubscription =
    subscription && (subscription.plan != null || subscription.id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <CreditCard className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Gerenciar Subscrição
              </h2>
              <p className="text-sm text-slate-400">
                {hasSubscription
                  ? "Alterar plano, status ou período"
                  : "Cliente sem subscrição — criar agora"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Plano
            </label>
            <div className="grid grid-cols-2 gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, plan: plan.id }))
                  }
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    formData.plan === plan.id
                      ? "border-red-500 bg-red-500/10"
                      : "border-white/10 hover:border-white/20 bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-white">
                      {plan.name}
                    </span>
                    <span className="text-sm text-slate-400">
                      R$ {plan.price}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, status: e.target.value }))
              }
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
            >
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10 transition-all"
            >
              Cancelar
            </button>
            {hasSubscription ? (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-red-500 rounded-lg text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
              >
                {loading ? "Salvando..." : "Salvar alterações"}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCreateSubscription}
                disabled={loading}
                className="px-4 py-2 bg-red-500 rounded-lg text-white font-medium hover:bg-red-600 transition-all disabled:opacity-50"
              >
                {loading ? "Criando..." : "Criar subscrição"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
